// Procedural low-poly 3D unit models: a serf (owner-coloured tunic + tool by "look")
// and the wild animals. Feet on y=0, +Y up, facing -Z (the layer yaws to movement).
// One merged geometry per (type/look/colour), cached. Owner: client-render3d/models.

import { BufferGeometry } from 'three'
import { MeshBuilder } from './mesh'
import { MAT } from './palette3d'

type Builder = (mb: MeshBuilder, color: string) => void

// Serf joint pivots (feet at y=0). The rig places each limb mesh here and rotates
// it about the pivot; the merged (gallery) figure draws the same limbs in place.
export const HIP_X = 1.9
export const HIP_Y = 6.9
export const SHOULDER_X = 4.4
export const SHOULDER_Y = 14.75

/** One leg as a box whose HIP sits at (ox,oy); pass (0,0) for a pivot-local part. */
function leg(mb: MeshBuilder, ox: number, oy: number): void {
  mb.box(ox, oy - 2.2, 0, 2.7, 4.4, 2.9, '#665747')
  mb.box(ox, oy - 5.2, -0.3, 2.8, 3.1, 3.5, MAT.woodDk)
  mb.box(ox, oy - 6.25, -0.8, 3, 1.2, 4.2, '#41382f')
}
/** One arm as a box whose SHOULDER sits at (ox,oy); pass (0,0) for a pivot-local part. */
function arm(mb: MeshBuilder, ox: number, oy: number, tunic: string): void {
  mb.gem(ox, oy - .35, 0, 3.1, 2.5, 3.2, tunic, 8) // shoulder joint stays inside the tunic
  mb.gem(ox, oy - 1.8, 0, 2.8, 4.5, 3, tunic)
  mb.box(ox, oy - 4.7, 0, 1.8, 2.6, 2, MAT.skin)
  mb.gem(ox, oy - 6.6, 0, 2.2, 2.1, 2.3, MAT.skin)
}

/** Torso, belt and a bare head — the rigid upper core, identical for every serf
 *  (no hats, helmets or body-worn gear: what a serf IS shows in its hands). */
function core(mb: MeshBuilder, tunic: string): void {
  // Broad, faceted shoulders and a full waist keep the tunic joined to the
  // sleeves and hips throughout a work stroke instead of tapering to loose tips.
  const ring = (y: number, w: number, d: number): number[][] => [
    [w * .7, y, -d], [w, y, -d * .65], [w, y, d * .65], [w * .7, y, d],
    [-w * .7, y, d], [-w, y, d * .65], [-w, y, -d * .65], [-w * .7, y, -d],
  ]
  const rings = [ring(7, 3.3, 2.25), ring(10.2, 3.5, 2.5), ring(13.7, 4.1, 2.6), ring(15, 3.6, 2.2)]
  for (let level = 0; level < rings.length - 1; level++) for (let i = 0; i < 8; i++) {
    const next = (i + 1) % 8
    mb.quad(rings[level][i], rings[level + 1][i], rings[level + 1][next], rings[level][next], tunic, .88 + (i % 3) * .04)
  }
  for (let i = 0; i < 8; i++) {
    const n = (i + 1) % 8, a = rings[0][i], b = rings[0][n], c = rings[3][i], d = rings[3][n]
    mb.tri(0, 7, 0, ...a as [number, number, number], ...b as [number, number, number], tunic, .7)
    mb.tri(0, 15, 0, ...d as [number, number, number], ...c as [number, number, number], tunic)
  }
  mb.box(0, 8.8, 0, 7.1, 1, 4.7, MAT.woodDk)
  mb.box(0, 8.8, -2.5, 1.3, 1.2, 0.5, MAT.gold)
  mb.box(0, 14, -2.1, 2.5, 1.6, 0.5, MAT.cloth)
  mb.cyl(0, 14.8, 0, 1.1, 1.6, 6, MAT.skin)
  mb.gem(0, 18, -0.1, 5.2, 5.6, 5, MAT.skin, 8)
  mb.gem(0, 19.9, 0.3, 5.4, 2.7, 5.1, MAT.woodDk, 8)
  mb.box(0, 19.3, 1.8, 4.2, 2.4, 1.3, MAT.woodDk)
  mb.gem(0, 17.7, -2.6, 1, 1.3, 1.3, MAT.skin, 4)
  for (const x of [-1.2, 1.2]) mb.box(x, 18.3, -2.35, 0.5, 0.5, 0.3, MAT.woodDk)
  // (a hauler's cargo is a resource-specific model held in the hands — see cargoGeometry)
}

// ---- hand-held tools -----------------------------------------------------------
// Each tool is authored around its GRIP at the origin (shaft along Y, business end
// up), so hanging it from the fist and tilting it about X poses it realistically:
// shaft tools rest back over the shoulder, the spear stands tall at the side, the
// sword hangs point-forward-down. Body-worn gear must never swing → core(), not here.

/** Fist-relative pose: rotation about X. Negative tips the head (+y) end FORWARD —
 *  the carry the game uses: shafts near-horizontal, high point leading the march. */
const TOOL_POSE: Record<string, number> = {
  spearman: -0.9, rod: -0.95, archer: -0.55,
  hammer: -1.05, pick: -1.05, axe: -1.05, hoe: -1.0, shovel: -1.0,
  swordsman: 2.05, // blade authored downward: >90° swings the point forward-up
}
/** Where the fist sits in the arm's pivot-local frame (arm hangs 0…−7.5).
 *  Slightly outboard (+x) so shafts clear the torso and head silhouette. */
export const HAND: { x: number; y: number; z: number } = { x: 0.8, y: -6.8, z: -0.6 }

function toolBuilder(look: string): ((mb: MeshBuilder) => void) | null {
  switch (look) {
    case 'spearman': return (mb) => {
      mb.cyl(0, -5, 0, 0.5, 26, 4, MAT.woodDk) // shaft: butt −5 … +21
      mb.pyramid(0, 21, 0, 1.1, 3.5, 4, MAT.woodPale) // fire-hardened wooden point
    }
    case 'swordsman': return (mb) => {
      mb.cyl(0, -1.2, 0, 0.55, 3.6, 4, MAT.woodDk) // hilt through the fist
      mb.box(0, -1.6, 0, 3.8, 1.1, 1.1, MAT.metalDk) // crossguard below the fist
      mb.box(0, -6.6, 0, 1.3, 9.5, 0.5, MAT.metal) // blade, point down
    }
    case 'archer': return (mb) => {
      mb.box(0, 0, -0.3, 1, 17, 1.3, MAT.woodDk) // bow stave in the fist
      mb.box(0, 0, 1.3, 0.4, 15.4, 0.4, MAT.cloth) // string toward the body
    }
    case 'hammer': return (mb) => {
      mb.cyl(0, -3, 0, 0.5, 16, 4, MAT.woodDk) // haft: −3 … +13, head above the shoulder
      mb.box(0, 12, 0, 4.2, 3, 3, MAT.woodPale) // carved mallet head (wood-only tool)
    }
    case 'pick': return (mb) => {
      mb.cyl(0, -3, 0, 0.5, 16, 4, MAT.woodDk)
      mb.box(0, 12.4, 0, 1.6, 1.6, 9, MAT.woodPale) // carved wooden head (wood-only tool)
    }
    case 'axe': return (mb) => {
      mb.cyl(0, -3, 0, 0.5, 16, 4, MAT.woodDk)
      mb.box(0, 11.4, -1.9, 1.2, 4.6, 2.8, MAT.woodPale) // carved wooden bit (wood-only tool)
    }
    case 'hoe': return (mb) => {
      mb.cyl(0, -3, 0, 0.5, 15, 4, MAT.woodDk)
      mb.box(0, 11.2, -1.7, 3, 1.4, 3, MAT.woodPale) // carved blade plate (wood-only tool)
    }
    case 'shovel': return (mb) => {
      mb.cyl(0, -3, 0, 0.5, 14, 4, MAT.woodDk)
      mb.box(0, 11.6, 0, 2.6, 3.4, 0.8, MAT.woodPale) // carved spade blade (wood-only tool)
    }
    case 'rod': return (mb) => {
      mb.cyl(0, -4, 0, 0.35, 20, 4, MAT.woodDk)
    }
  }
  return null
}

/** The posed, fist-gripped tool for the rig: geometry (grip at origin) + pose.
 *  Attach as a child of the right-arm mesh at HAND with rotation.x = rx. */
export function heldTool(look: string): { geo: BufferGeometry; rx: number } | null {
  const tb = toolBuilder(look)
  if (!tb) return null
  return { geo: cached(`tool:${look}`, tb), rx: TOOL_POSE[look] ?? 0 }
}

/** How much the tool-holding right arm swings: a free hand swings fully, a light tool
 *  a little (reads as carried weight), a long/pointy weapon not at all (its tip would
 *  otherwise sweep the ground). Left arm is always free (full swing). */
const TOOL_SWING: Record<string, number> = {
  spearman: 0, archer: 0, rod: 0, pick: 0,
  hammer: 0.45, axe: 0.45, hoe: 0.45, shovel: 0.45, swordsman: 0.45, carrier: 0.45,
}
export function toolArmScale(look: string): number { return TOOL_SWING[look] ?? 1 }

/** A basic standing figure: legs, tunic torso, both arms, bare head, hand tool. Used
 *  for the merged (static) serf — the gallery and any non-rigged fallback. */
function figure(mb: MeshBuilder, tunic: string, look: string): void {
  leg(mb, -HIP_X, HIP_Y)
  leg(mb, HIP_X, HIP_Y)
  arm(mb, -SHOULDER_X, SHOULDER_Y, tunic) // left arm
  arm(mb, SHOULDER_X, SHOULDER_Y, tunic)  // right arm
  core(mb, tunic)
  const tb = toolBuilder(look) // gripped in the right fist, posed like the rig's
  if (tb) {
    const t = new MeshBuilder()
    tb(t)
    mb.merge(t, SHOULDER_X + HAND.x, SHOULDER_Y + HAND.y, HAND.z, TOOL_POSE[look] ?? 0)
  }
}

const serf: (look: string) => Builder = (look) => (mb, color) => {
  figure(mb, color, look)
}

const deer: Builder = (mb) => {
  const tan = '#a9743f'
  mb.gem(0, 9, 0, 7.5, 8, 18, tan, 8)
  for (const [x, z] of [[-2, -6], [2, -6], [-2, 6], [2, 6]]) mb.box(x, 3, z, 1.6, 8, 1.6, MAT.woodDk)
  mb.gem(0, 12.5, -7, 4, 9, 5, tan)
  mb.gem(0, 16, -10, 4.4, 4.6, 6, tan)
  for (const s of [-1, 1]) {
    mb.gem(s * 2.5, 17.6, -9.5, 3.6, 1.4, 2, tan, 4)
    mb.beam([s * 1.3, 18, -10], [s * 2.6, 23, -8], 0.6, MAT.woodPale)
    mb.beam([s * 2, 20, -9.2], [s * 4, 22, -10.5], 0.5, MAT.woodPale)
  }
  mb.box(0, 11, 8.5, 1.4, 3, 1.4, tan) // tail
}

const wolf: Builder = (mb) => {
  const grey = '#8a8f96'
  mb.gem(0, 7, 0, 7, 7, 17, grey, 8)
  mb.gem(0, 8, -4, 7.5, 8, 7, MAT.slate)
  for (const [x, z] of [[-1.6, -5], [1.6, -5], [-1.6, 5], [1.6, 5]]) mb.box(x, 3, z, 1.5, 6, 1.5, MAT.slate)
  mb.gem(0, 9, -8, 4.5, 4.8, 5, grey)
  mb.gem(0, 8.3, -10.5, 2.7, 2.5, 4, grey)
  mb.gem(0, 8.5, -12.4, 1.6, 1.2, 1, MAT.coal)
  for (const s of [-1, 1]) mb.pyramid(s * 1.4, 10.3, -7.8, 1.1, 3, 4, MAT.slate)
  mb.beam([0, 8, 7], [0, 5.5, 13], 2.2, grey)
}

const bear: Builder = (mb) => {
  const brown = '#6b4a30'
  mb.gem(0, 10, 0, 13, 14, 22, brown, 8)
  mb.gem(0, 12, -5, 12, 14, 11, brown, 8)
  for (const [x, z] of [[-3, -6], [3, -6], [-3, 6], [3, 6]]) mb.box(x, 4, z, 3, 9, 3, '#553c26')
  mb.gem(0, 13, -10, 7.5, 7.5, 7, brown, 8)
  for (const x of [-2.5, 2.5]) mb.gem(x, 16.6, -9.5, 2.5, 2.7, 2, '#553c26')
  mb.gem(0, 12, -13, 3.6, 3, 3.5, '#9e805c')
  mb.gem(0, 12.6, -14.8, 2, 1.5, 1.2, MAT.coal)
}

const ANIMALS: Record<string, Builder> = { deer, wolf, bear }
const cache = new Map<string, BufferGeometry>()

function cached(key: string, build: (mb: MeshBuilder) => void): BufferGeometry {
  let g = cache.get(key)
  if (g) return g
  const mb = new MeshBuilder()
  build(mb)
  g = mb.geometry()
  cache.set(key, g)
  return g
}

/** Cached merged geometry for a unit. Animals use their builder; everyone else is a
 *  (static, merged) serf keyed by `look`+colour — used for the gallery and fallback. */
export function unitGeometry(type: string, look: string, color: string): BufferGeometry {
  const animal = ANIMALS[type]
  if (animal) return cached(type, (mb) => animal(mb, ''))
  return cached(`c:${look}:${color}`, (mb) => serf(look)(mb, color))
}

/** Separate limb geometries for the animated serf rig. Each limb is authored in its
 *  own pivot-local frame (joint at the origin, hanging down -y) so the entity layer
 *  can rotate it about the hip/shoulder. Geometries are cached & shared: the leg is
 *  identical for every serf, the core and free (left) arm vary only by tunic colour,
 *  the tool-bearing right arm by look. */
export interface SerfRig { core: BufferGeometry; armL: BufferGeometry; armR: BufferGeometry; leg: BufferGeometry }
export function serfRig(look: string, color: string): SerfRig {
  return {
    core: cached(`core:${color}`, (mb) => core(mb, color)),
    armL: cached(`arm:${color}`, (mb) => arm(mb, 0, 0, color)),
    armR: cached(`arm:${color}`, (mb) => arm(mb, 0, 0, color)),
    leg: cached('leg', (mb) => leg(mb, 0, 0)),
  }
}

// The load a hauler carries in both hands, in front of the belly (~y10, z-6, faces -Z).
// A distinct low-poly model per resource so you can read a serf's cargo at a glance.
function buildCargo(mb: MeshBuilder, res: string): void {
  const z = -6
  switch (res) {
    case 'wood': // a bundle of logs across the arms, pale cut ends facing out
      mb.box(-2.2, 9.4, z, 4.4, 2.3, 2.4, MAT.wood)
      mb.box(2.2, 9.4, z, 4.4, 2.3, 2.4, MAT.timber)
      mb.box(0, 11.4, z, 4.6, 2.3, 2.4, MAT.woodDk)
      mb.box(-4.4, 9.4, z, 0.5, 2, 2.1, MAT.thatch)
      mb.box(4.4, 9.4, z, 0.5, 2, 2.1, MAT.thatch)
      mb.box(2.3, 11.4, z, 0.5, 2, 2.1, MAT.thatch); break
    case 'stone': // a few rough grey rocks, varied and tumbled
      mb.box(0, 9.6, z, 5, 4.6, 4.4, MAT.stone, 0.3)
      mb.box(2.4, 11, z - 0.4, 3, 3, 3, MAT.stoneDk, -0.4)
      mb.box(-2.2, 9.4, z + 0.5, 2.6, 2.6, 2.6, MAT.slate, 0.5); break
    case 'food': // a woven basket heaped with golden grain
      mb.cyl(0, 8, z, 3.4, 4.2, 8, MAT.dirt)
      mb.box(0, 12.1, z, 6, 1.5, 5, MAT.crop)
      mb.pyramid(0, 12.6, z, 2.6, 1.8, 6, MAT.crop); break
    case 'iron': // dark ore chunks with a metallic glint
      mb.box(0, 9.6, z, 4.6, 4, 4.2, MAT.ironOre, 0.3)
      mb.box(2, 10.8, z - 0.4, 2.6, 2.6, 2.6, MAT.metalDk, -0.4)
      mb.box(-1.8, 9.3, z + 0.4, 2.4, 2.4, 2.4, MAT.slate, 0.5)
      mb.box(0.6, 11.5, z, 1.3, 1.3, 1.3, MAT.metal, 0.3); break
    case 'gold': // stacked gold ingots
      mb.box(-1.6, 9.2, z, 3, 1.8, 3, MAT.gold)
      mb.box(1.6, 9.2, z, 3, 1.8, 3, MAT.gold)
      mb.box(0, 11, z, 3.2, 1.8, 2.8, MAT.gold); break
    case 'dirt': // a heaped barrow-load of earth
      mb.box(0, 9.2, z, 5.6, 3, 4.4, MAT.soil, 0.15)
      mb.box(0.8, 11.2, z - 0.3, 3.2, 1.8, 2.6, MAT.dirt, -0.3); break
    case 'sand': // pale heaped sand
      mb.box(0, 9.2, z, 5.6, 3, 4.4, '#d8c48e', 0.15)
      mb.box(-0.8, 11.2, z + 0.3, 3.2, 1.8, 2.6, '#c9b489', -0.3); break
    default: // unknown goods — a plain crate
      mb.box(0, 10, z, 6, 5, 4, MAT.cloth); break
  }
}

/** Cached cargo model for a hauling serf, held in the hands. */
export function cargoGeometry(res: string): BufferGeometry {
  return cached(`cargo:${res}`, (mb) => buildCargo(mb, res))
}
